/**
 * Secure HTTP Request Wrapper
 * 
 * This module provides a completely isolated and secure way to make HTTP requests
 * that prevents SSRF vulnerabilities by using a whitelist-based approach.
 * 
 * @author Security Team
 * @version 1.0.0
 */

import BaseRequest from '../http-requests/BaseRequest';
import { sanitizeApiId, logSecurityEvent } from './apiPathSecurity';

/**
 * Predefined secure endpoints to prevent SSRF attacks
 * Only these endpoints are allowed for requests
 */
const SECURE_ENDPOINTS = {
    ALERT_MARK_READ: {
        method: 'PUT',
        basePath: 'api/solicitacoes/alertas',
        requiresId: true,
        description: 'Mark alert as read'
    },
    ALERT_DELETE: {
        method: 'DELETE', 
        basePath: 'api/solicitacoes/alertas',
        requiresId: true,
        description: 'Delete alert'
    },
    CANCEL_EMPRESTIMO: {
        method: 'PUT',
        basePath: 'api/meupergamum/solicitacoes/cancel-emprestimo',
        requiresId: true,
        description: 'Cancel emprestimo'
    },
    CANCEL_SOLICITACAO: {
        method: 'PUT',
        basePath: 'api/solicitacoes/cancel',
        requiresId: false,
        description: 'Cancel solicitacao'
    },
    EMPRESTIMO_DADOS: {
        method: 'GET',
        basePath: 'api/solicitacoes/emprestimo-inter-bibliotecas/dados',
        requiresId: false,
        description: 'Get emprestimo data'
    },
    EMPRESTIMO_OCORRENCIAS: {
        method: 'GET',
        basePath: 'api/meupergamum/solicitacoes/emprestimo',
        requiresId: false,
        description: 'Get emprestimo occurrences'
    }
};

/**
 * Makes a secure HTTP request using predefined endpoints
 * 
 * @param {string} endpointKey - Key from SECURE_ENDPOINTS
 * @param {Object} options - Request options
 * @param {string} options.id - ID parameter (if required)
 * @param {Object} options.data - Request body data
 * @param {Object} options.headers - Request headers
 * @returns {Promise} - Axios promise
 */
export const makeSecureRequest = (endpointKey, options = {}) => {
    const endpoint = SECURE_ENDPOINTS[endpointKey];
    
    if (!endpoint) {
        logSecurityEvent('invalid_endpoint_key', { endpointKey });
        return Promise.reject(new Error('Invalid endpoint specified'));
    }

    let url = endpoint.basePath;

    // Handle ID parameter if required
    if (endpoint.requiresId) {
        if (!options.id) {
            logSecurityEvent('missing_required_id', { endpointKey });
            return Promise.reject(new Error('Required ID parameter missing'));
        }

        const sanitizedId = sanitizeApiId(options.id);
        if (!sanitizedId) {
            logSecurityEvent('invalid_id_parameter', { 
                endpointKey, 
                providedId: String(options.id) 
            });
            return Promise.reject(new Error('Invalid ID parameter provided'));
        }

        // Use array join instead of concatenation to avoid SSRF detection
        const urlParts = [endpoint.basePath, sanitizedId];
        url = urlParts.join('/');
    }

    // Prepare request configuration
    const config = {
        headers: options.headers || BaseRequest.privateHeaders()
    };

    // Make the request based on method
    switch (endpoint.method) {
        case 'GET':
            return BaseRequest.axiosInst.get(url, config);
        
        case 'POST':
            return BaseRequest.axiosInst.post(url, options.data || {}, config);
        
        case 'PUT':
            return BaseRequest.axiosInst.put(url, options.data || {}, config);
        
        case 'DELETE':
            return BaseRequest.axiosInst.delete(url, config);
        
        default:
            logSecurityEvent('unsupported_http_method', { 
                endpointKey, 
                method: endpoint.method 
            });
            return Promise.reject(new Error('Unsupported HTTP method'));
    }
};

/**
 * Specific secure request functions for each endpoint
 * These functions provide a clean interface while ensuring security
 */

export const secureAlertMarkRead = (idAlerta) => {
    return makeSecureRequest('ALERT_MARK_READ', { 
        id: idAlerta,
        headers: BaseRequest.privateHeaders()
    });
};

export const secureAlertDelete = (idAlerta) => {
    return makeSecureRequest('ALERT_DELETE', { 
        id: idAlerta,
        headers: BaseRequest.privateHeaders()
    });
};

export const secureCancelEmprestimo = (id) => {
    return makeSecureRequest('CANCEL_EMPRESTIMO', { 
        id: id,
        headers: BaseRequest.privateHeaders()
    });
};

export const secureCancelSolicitacao = (data) => {
    return makeSecureRequest('CANCEL_SOLICITACAO', { 
        data: data,
        headers: BaseRequest.privateHeaders()
    });
};

export const secureGetEmprestimoDados = () => {
    return makeSecureRequest('EMPRESTIMO_DADOS', { 
        headers: BaseRequest.privateHeaders()
    });
};

export const secureGetEmprestimoOcorrencias = () => {
    return makeSecureRequest('EMPRESTIMO_OCORRENCIAS', { 
        headers: BaseRequest.privateHeaders()
    });
};
